import {
  buildDataset,
  type ExportColumn,
  type ExportDataset,
  type ExportMetadata,
} from "@fleet/export";

/**
 * Timesheet adapter: maps aggregated timesheet rows into the generic
 * {@link ExportDataset}. This is the ONLY domain-specific export piece in the
 * time-tracker clone — it replaces the canon `todo-adapter` (K1 propagation).
 *
 * A {@link TimesheetRow} is one already-aggregated bucket: time grouped by
 * project (and optionally task/tag) over a date range, with the billable amount
 * derived from the hourly rate. Hours and amounts are pre-computed numbers so the
 * serializers stay dumb; we only format them to fixed decimals here.
 */

const HOURS_DECIMALS = 2;
const AMOUNT_DECIMALS = 2;

/** One aggregated timesheet line, ready to export. */
export interface TimesheetRow {
  /** Project display name. */
  project: string;
  /** Optional task name within the project. */
  task?: string | null;
  /** Optional comma-separated tag names. */
  tags?: string | null;
  /** Period label for the bucket (e.g. "2026-06-18", or "2026-06-15 – 2026-06-21"). */
  period: string;
  /** Total tracked time in decimal hours (e.g. 1.5 = 1h30m). */
  hours: number;
  /** Hourly rate applied to this row, in the report currency. 0 = not billable. */
  rate: number;
  /** Billable amount = hours × rate. */
  amount: number;
  /** Currency of the (sealed) rate/amount, when known (rate-sealing). */
  currency?: string | null;
}

export const EXPORT_COLUMNS: ExportColumn<TimesheetRow>[] = [
  { key: "project", header: "Project", accessor: (r) => r.project },
  { key: "task", header: "Task", accessor: (r) => r.task ?? "" },
  { key: "tags", header: "Tags", accessor: (r) => r.tags ?? "" },
  { key: "period", header: "Period", accessor: (r) => r.period },
  { key: "hours", header: "Hours", accessor: (r) => r.hours.toFixed(HOURS_DECIMALS) },
  { key: "rate", header: "Rate", accessor: (r) => r.rate.toFixed(AMOUNT_DECIMALS) },
  { key: "amount", header: "Amount", accessor: (r) => r.amount.toFixed(AMOUNT_DECIMALS) },
];

/**
 * Builds an {@link ExportDataset} from aggregated timesheet rows, ready for
 * `datasetToCsv` / `datasetToHtml`. Appends a TOTAL row summing hours + amount.
 */
export function buildTimesheetDataset(
  rows: readonly TimesheetRow[],
  metadata: ExportMetadata = {},
): ExportDataset {
  const dataset = buildDataset(EXPORT_COLUMNS, rows, {
    title: "Timesheet",
    ...metadata,
  });

  // Append a TOTAL footer row (hours + amount columns summed; the rest blank).
  const totalHours = rows.reduce((sum, r) => sum + r.hours, 0);
  const totalAmount = rows.reduce((sum, r) => sum + r.amount, 0);
  dataset.rows.push([
    "TOTAL",
    "",
    "",
    "",
    totalHours.toFixed(HOURS_DECIMALS),
    "",
    totalAmount.toFixed(AMOUNT_DECIMALS),
  ]);

  return dataset;
}
